// Page-scoped MAVIS suggestions and proactive insight chips. Pure functions —
// no data fetches here. Adding a new admin page is one entry in this switch.
import type { PageContextValue } from "./PageContext";

export interface InsightChip {
  label: string;
  /** Question that gets dropped into the composer when the chip is clicked. */
  prompt: string;
  tone?: "default" | "warning";
}

export function suggestionsForPage(ctx: PageContextValue): string[] {
  const sel = ctx.selection;
  const selRef = sel ? `${sel.label}` : null;

  switch (ctx.section) {
    case "instructors":
      return selRef
        ? [
            `What's still missing for ${selRef}?`,
            `Show ${selRef}'s onboarding status`,
            `Assign ${selRef} to a class`,
            `Record a one-time payment for ${selRef}`,
          ]
        : [
            "Which instructors are missing W-9s?",
            "Which instructors aren't ready to teach?",
            "Start onboarding for a new instructor",
            "Show contractors with expiring background checks",
          ];
    case "registrations":
      return [
        "Show unpaid registrations this month",
        "Who signed up in the last 24 hours?",
        "Which classes are nearly full?",
        "Move a registration to another class",
      ];
    case "classes":
      return [
        "Which classes still need an instructor?",
        "Show classes nearing capacity",
        "Which coming-soon classes hit the interest threshold?",
        "What changed on the schedule this week?",
      ];
    case "payroll":
      return [
        "What's ready to pay this cycle?",
        "Explain any ledger exceptions",
        "Which instructors are still unpaid for the last closed period?",
        "Show payroll totals by semester",
      ];
    case "popup":
      return [
        "Which vendors haven't paid?",
        "How many early-bird tickets sold?",
        "Which vendors are still missing details?",
        "Draft a reminder email to unpaid vendors",
      ];
    case "waiting":
      return [
        "What am I waiting on right now?",
        "Anything overdue?",
        "Add a new waiting item",
      ];
    case "action_center":
      return [
        "What's the highest-priority recommendation?",
        "Dismiss any resolved recommendations",
        "Explain why #1 is on the list",
      ];
    case "knowledge":
      return [
        "Search the knowledge vault",
        "Add a new knowledge article",
        "What does MAVIS remember about payroll?",
      ];
    case "settings":
      return [
        "Rename a registration period",
        "Archive an old registration period",
        "Which periods are visible right now?",
      ];
    case "reports":
      return [
        "Give me revenue year-to-date",
        "Show refunds this month",
        "Which classes are most profitable?",
      ];
    case "dashboard":
    default:
      return [
        "What needs my attention today?",
        "Give me the morning brief",
        "What's ready to pay?",
        "Which classes hit their interest threshold?",
      ];
  }
}

export function proactiveInsightsForPage(ctx: PageContextValue): InsightChip[] {
  switch (ctx.section) {
    case "instructors":
      return [
        { label: "Missing W-9s", prompt: "List instructors missing a W-9.", tone: "warning" },
        { label: "Not ready to teach", prompt: "Which instructors aren't ready to teach?", tone: "warning" },
        { label: "Expiring background checks", prompt: "Which background checks are expiring in the next 60 days?" },
      ];
    case "registrations":
      return [
        { label: "Hasn't paid yet", prompt: "Show families who registered but haven't paid this month.", tone: "warning" },
        { label: "New in last 24h", prompt: "Which families signed up in the last 24 hours?" },
        { label: "Session needs confirming", prompt: "Are any families in the wrong session for their class?", tone: "warning" },
      ];

    case "classes":
      return [
        { label: "No instructor", prompt: "Which classes still need an instructor?", tone: "warning" },
        { label: "Near capacity", prompt: "Which sessions are 80%+ full?" },
        { label: "Interest threshold hit", prompt: "Which coming-soon classes hit the interest threshold?" },
      ];
    case "payroll":
      return [
        { label: "Ready to pay", prompt: "What's ready to pay right now?" },
        { label: "Exceptions", prompt: "Explain any ledger exceptions.", tone: "warning" },
      ];
    case "popup":
      return [
        { label: "Unpaid vendors", prompt: "Which pop-up vendors still owe their table fee?", tone: "warning" },
        { label: "Early-bird sales", prompt: "How many early-bird tickets have sold so far?" },
      ];
    case "waiting":
      return [
        { label: "Overdue", prompt: "What waiting items are overdue?", tone: "warning" },
      ];
    case "action_center":
      return [
        { label: "Highest priority", prompt: "What's the top-priority recommendation right now?" },
      ];
    case "dashboard":
    default:
      return [
        { label: "Morning brief", prompt: "Give me the morning brief." },
        { label: "Needs attention", prompt: "What needs my attention today?" },
      ];
  }
}
